import React = require('react');
import IconTextButton from "diy/custom-components/icon-text-button/IconTextButton";
import {Dropdown} from './toolbar/Dropdown';
import {Divider} from './toolbar/Divider';
import {ToolbarGroup} from './toolbar/ToolbarGroup';
import {ToolbarContent} from './toolbar/ToolbarContent';
import {LayoutRotate} from './basic/LayoutRotate';
import TogglesView, {ToggleGroups} from "./basic/TogglesView";
import FullWidthView from "./basic/FullWidthView";
import BackgroundSelectorView, {Tab} from "./common/BackgroundSelectorView";
import {BackgroundSettings} from "../state";
import l10n = require('diy/util/l10n');
import features = require('diy/util/features');
import {autoprefixer} from "diy/material-ui/autoprefixer";

import VisibilityIcon from 'material-ui/svg-icons/action/visibility';
import BgPhotoIcon from 'material-ui/svg-icons/image/photo-size-select-actual';
import ViewModuleIcon from 'material-ui/svg-icons/action/view-module';
import ClearIcon from 'material-ui/svg-icons/content/clear';
import AddBoxIcon from 'material-ui/svg-icons/content/add-box';
import AspectRatioIcon from 'material-ui/svg-icons/action/aspect-ratio';
import PaletteIcon from 'material-ui/svg-icons/image/palette';

import {trackBackgroundColorClicked, trackBackgroundTabClicked, trackBackgroundImageEdited} from "../tracking";
import {Messages} from './common/Messages';
import {ConfigurableGroups} from "../../sectionContainer/index";
import {ColorType} from './common/ColorSelectorView';
import {ConnectedAviary} from './aviary/ConnectedAviary';

export interface StateProps {
    availableLayouts: string[];
    toggles: ToggleGroups[];
    layout: string;
    fullWidth: boolean;
    isFullWidthAvailable: boolean;
    isManageGroupsAvailable: boolean;
    isAddGroupAvailable: boolean;
    canAddGroup: boolean;
    canDeleteGroup: boolean;
    background: BackgroundSettings;
    groupBackgroundMap?: {
        [groupId: string]: BackgroundSettings;
    };
    configurableGroups: ConfigurableGroups;
    configurableGroupIds: number[];
    manageGroupsActive: boolean;
    elementsMenuVisible: boolean;
    backgroundMenuVisible: boolean;
}

export interface DispatchProps {
    onChangeLayout?: (layoutName: string) => void;
    onChangeFullWidth?: (fullWidth: boolean) => void;
    onChangeToggles?: (toggles: ToggleGroups[]) => void;
    onBackgroundChange?: (background: BackgroundSettings) => void;
    onManageGroupsToggle?: (active: boolean) => void;
    onBackgroundImageChange(
        imageId: number,
        imageType: string,
        successCallback: () => void,
        errorCallback: () => void
    ): void;
    onGroupColorClicked?(backgroundColorType: ColorType, value?: string): void;
    onGroupTabClicked?(tab: Tab): void;
    onChangeGroupBackgroundImage?(
        moduleId: number,
        imageId: number,
        imageType: string,
        successCallback: () => void,
        errorCallback: () => void
    ): void;
    onAddGroup(): void;
    onChangeGroupBackground?(moduleId: number, background: BackgroundSettings): void;
    onElementsMenuToggle(payload: {visible: boolean}): void;
    onBackgroundMenuToggle(payload: {visible: boolean}): void;
    onEditImageClicked(): void;
    onSelectImageClicked(): void;
}

export interface EditViewProps extends React.Props<EditView>, StateProps, DispatchProps {
}
interface EditViewState {}

export default class EditView
    extends React.Component<EditViewProps, EditViewState> {

    render() {
        let isManageGroupsActive = this.props.manageGroupsActive;

        let hasOneConfigurableGroup = !this.props.isAddGroupAvailable && this.props.isManageGroupsAvailable;
        let onlyColor = false;
        if (hasOneConfigurableGroup) {
            let configurables = this.props.configurableGroups;
            let configurable = configurables[this.props.configurableGroupIds[0]];
            onlyColor = configurable.color && !configurable.image && !configurable.video;
        }
        let groupBackground: BackgroundSettings;
        if (hasOneConfigurableGroup) {
            groupBackground = this.props.groupBackgroundMap[this.props.configurableGroupIds[0]];
        }

        return (
            <div>
                <ToolbarContent>
                    <ToolbarGroup>
                        <Dropdown
                            text={l10n.getString('section.actionBar.buttons.contentToggles.label')}
                            Icon={<VisibilityIcon />}
                            disabled={isManageGroupsActive}
                            open={this.props.elementsMenuVisible}
                            dataTestId="section-toggle-content"
                            layerStyle={{
                                marginTop: 7,
                                padding: 16,
                                maxWidth: 300
                            }}
                            onClose={() => {
                                this.props.onElementsMenuToggle({visible: false});
                            }}
                            onOpen={() => {
                                this.props.onElementsMenuToggle({visible: true});
                            }}
                        >
                            <TogglesView
                                onToggleSwitch={this.props.onChangeToggles}
                                toggles={this.props.toggles}
                            />
                        </Dropdown>
                        <Dropdown
                            text={l10n.getString('section.actionBar.buttons.backgroundSettings.label')}
                            Icon={<BgPhotoIcon />}
                            open={this.props.backgroundMenuVisible}
                            disabled={isManageGroupsActive}
                            layerStyle={{
                                marginTop: 7,
                                padding: 0,
                                minWidth: 240
                            }}
                            onClose={() => {
                                this.props.onBackgroundMenuToggle({visible: false});
                            }}
                            onOpen={() => {
                                this.props.onBackgroundMenuToggle({visible: true});
                            }}
                            dataTestId="section-change-background"
                        >
                            <BackgroundSelectorView
                                background={this.props.background}
                                onChange={this.props.onBackgroundChange}
                                onImageChange={this.props.onBackgroundImageChange}
                                onEditImageClicked={() => this.onEditImageClicked()}
                                onSelectImageClicked={this.props.onSelectImageClicked}
                                onColorClicked={trackBackgroundColorClicked}
                                onTabClicked={trackBackgroundTabClicked}
                                parallaxAvailable={true}
                            />
                        </Dropdown>
                        {!this.props.isAddGroupAvailable &&
                            !features('sectionGroups') &&
                            this.props.isManageGroupsAvailable ?
                            <IconTextButton
                                style={{marginRight: 12}}
                                text={l10n.getString('section.actionBar.buttons.manageGroups.label')}
                                Icon={isManageGroupsActive ? <ClearIcon /> : <ViewModuleIcon />}
                                isPressed={isManageGroupsActive}
                                onTouchTap={() => this.props.onManageGroupsToggle(!this.props.manageGroupsActive)}
                                useAlternativeColors={true}
                                dataTestId="section-manage-groups-button"
                                />
                            : null}
                        {features('sectionGroups') && hasOneConfigurableGroup ?
                            <Dropdown
                                text={l10n.getString('section.actionBar.buttons.overlayColor.label')}
                                Icon={<PaletteIcon />}
                                layerStyle={{
                                    marginTop: 7,
                                    padding: 0,
                                    minWidth: 240
                                }}
                                dataTestId="section-change-group-background"
                            >
                                <BackgroundSelectorView
                                    onlyColor={onlyColor}
                                    onChange={this.createChangeGroupBgHandler(this.props.configurableGroupIds[0])}
                                    onImageChange={this.createChangeGroupBgImgHandler(
                                        this.props.configurableGroupIds[0]
                                    )}
                                    onEditImageClicked={this.props.onEditImageClicked}
                                    onSelectImageClicked={this.props.onSelectImageClicked}
                                    background={groupBackground}
                                    onColorClicked={this.props.onGroupColorClicked}
                                    onTabClicked={this.props.onGroupTabClicked}
                                    parallaxAvailable={false}
                                    />
                            </Dropdown>
                            : null}

                        <Divider />
                        <LayoutRotate
                            activeLayout={this.props.layout}
                            availableLayouts={this.props.availableLayouts}
                            disabled={isManageGroupsActive}
                            onChange={layout => this.props.onChangeLayout(layout)}
                            dataTestId="section-change-layout-button"
                        />
                    </ToolbarGroup>
                    {this.props.isFullWidthAvailable ?
                        <ToolbarGroup>
                            <Divider />
                            <Dropdown
                                text={l10n.getString('section.actionBar.buttons.fullWidth.label')}
                                Icon={<AspectRatioIcon />}
                                buttonStyle={autoprefixer({ flexBasis: 0 })}
                                layerStyle={{
                                    marginTop: 7,
                                    padding: 8,
                                    maxWidth: 300
                                }}
                                disabled={isManageGroupsActive}
                                dataTestId="section-full-width"
                                >
                                <FullWidthView
                                    onToggleSwitch={this.props.onChangeFullWidth}
                                    isToggled={this.props.fullWidth}
                                    />
                            </Dropdown>
                        </ToolbarGroup> : null
                    }
                    {this.props.isAddGroupAvailable ?
                        <ToolbarGroup>
                            <Divider />
                            <IconTextButton
                                style={{ marginRight: 12 }}
                                text={l10n.getString('section.actionBar.buttons.addGroup.label')}
                                Icon={<AddBoxIcon />}
                                onTouchTap={() => this.props.onAddGroup()}
                                useAlternativeColors={true}
                                dataTestId="section-add-group-button"
                                isDisabled={!this.props.canAddGroup}
                                />
                        </ToolbarGroup> : null
                    }
                </ToolbarContent>
                <Messages showAddGroup={this.props.isAddGroupAvailable && !this.props.canAddGroup} />
                {<ConnectedAviary />}
            </div>
        );
    }

    private onEditImageClicked() {
        trackBackgroundImageEdited();
        this.props.onEditImageClicked();
    }

    private createChangeGroupBgImgHandler = (moduleId: number) => (
        imageId: number,
        imageType: string,
        successCallback: () => void,
        errorCallback: () => void
    ) => {
        this.props.onChangeGroupBackgroundImage(
            moduleId, imageId, imageType, successCallback, errorCallback
        );
    };

    private createChangeGroupBgHandler = (moduleId: number) => (
        background: BackgroundSettings
    ) => {
        this.props.onChangeGroupBackground(moduleId, background);
    };
}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/module/section/components/EditView.tsx
 **/